import * as angular from 'angular';
import * as _ from 'lodash';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { ICourseDiff } from '../../../ng2/shared/typings/interfaces/course-diff.interface';
import { IGapPlan } from '../../../ng2/shared/typings/interfaces/gap-plan.interface';
import { ISchool } from '../../../ng2/shared/typings/interfaces/school.interface';
import { IStudent } from '../../../ng2/shared/typings/interfaces/student.interface';
import { ISchoolSupport } from '../../../ng2/shared/typings/interfaces/support.interface';
import { StudentSetService } from '../../shared/services/sorter/student-set.service';
import {
  BulkUpdateAttendanceStudentsSuccess,
  BulkUpdateMyStudentsSuccess,
  BulkUpdateStudentsSuccess,
} from './../../../ng2/store/actions/students-actions';
import { TValidSdcColumns } from './../sdc-columns.type';
import { IColumnSort } from './../sdc-state/sdc-state.typings';
import { sdcActionsConstant } from './sdc-actions.constant';
import { ISdcStore, ISdcStoreState } from './sdc-store.interface';

export const SdcStoreFactory = function(
  StudentSet: StudentSetService,
  UtilitiesService: UtilitiesService,
  SDCStoreValidator,
  PublicConfig,
  SDCStoreSchoolChannel,
  Rollbar,
  Student,
  EventBus,
) {
  const handlers = {};

  handlers[sdcActionsConstant.SET_FILTER_FOR_COLUMN] = 'setFilterForColumn';
  handlers[sdcActionsConstant.CLEAR_FILTER_FOR_COLUMN] = 'clearFilterForColumn';
  handlers[sdcActionsConstant.SET_ENTIRE_FILTER] = 'setEntireFilter';
  handlers[sdcActionsConstant.CLEAR_ALL_UNLOCKED_FILTERS] = 'clearAllUnlockedFilters';
  handlers[sdcActionsConstant.ADD_FILTERS] = 'addFilters';
  handlers[sdcActionsConstant.ADD_LOCKED_FILTERS] = 'addLockedFilters';
  handlers[sdcActionsConstant.SET_LOCKED_FILTERS] = 'setLockedFilters';
  handlers[sdcActionsConstant.UNLOCK_ALL_FILTERS] = 'unlockAllFilters';
  handlers[sdcActionsConstant.SET_SELECTED_STUDENT_IDS] = 'setSelectedStudentIds';
  handlers[sdcActionsConstant.UNSELECT_ALL_STUDENTS] = 'unselectAllStudents';
  handlers[sdcActionsConstant.SELECT_ALL_STUDENTS] = 'selectAllStudents';
  handlers[sdcActionsConstant.SELECT_FILTERED_STUDENTS] = 'selectFilteredStudents';
  handlers[sdcActionsConstant.SET_STUDENTS_COLUMN_KEYS_SCHOOL_SORT_AND_FILTER] =
    'setStudentsColumnKeysSchoolSortAndFilter';
  handlers[sdcActionsConstant.UPDATE_STUDENT_FIELD_BY_PATH] = 'updateStudentFieldByPath';
  handlers[sdcActionsConstant.UPDATE_UI_STATE] = 'updateUiState';
  handlers[sdcActionsConstant.APPEND_COLUMN_DATA] = 'appendColumnData';
  handlers[sdcActionsConstant.SET_RECENT_EDITED_STUDENT_IDS] = 'setRecentEditedStudentIds';
  handlers[sdcActionsConstant.SET_SORT] = 'setSort';
  handlers[sdcActionsConstant.ADD_PENDING_SAVE] = 'addPendingSave';
  handlers[sdcActionsConstant.REMOVE_PENDING_SAVE] = 'removePendingSave';
  handlers[sdcActionsConstant.SET_SDC_ACTIVITY] = 'setSdcActivity';
  handlers[sdcActionsConstant.MULTI_STUDENT_UPDATE] = 'multiStudentUpdate';
  handlers[sdcActionsConstant.MULTI_STUDENT_PATCH] = 'multiStudentPatch';
  handlers[sdcActionsConstant.UPDATE_SCHOOL_BY_PATCH] = 'updateSchoolByPatch';
  handlers[sdcActionsConstant.SET_CURRENT_SDC_STATE] = 'setCurrentSdcState';
  handlers[sdcActionsConstant.RESET_FLATTENED_STUDENTS] = 'resetFlattenedStudents';
  handlers[sdcActionsConstant.RESET_CURRENT_SDC_STATE] = 'resetCurrentSdcState';
  handlers[sdcActionsConstant.MERGE_USER_ADDED_COLUMNS] = 'mergeUserAddedSdcColumns';
  handlers[sdcActionsConstant.ADD_COURSE_DIFFS] = 'addCourseDiffs';
  handlers[sdcActionsConstant.ADD_GAP_PLANS] = 'addGapPlans';
  handlers[sdcActionsConstant.UPDATE_COURSE_DIFF] = 'updateCourseDiff';
  handlers[sdcActionsConstant.SET_ALL_SCHOOL_USERS] = 'setAllSchoolUsers';
  handlers[sdcActionsConstant.ADD_SCHOOL_SUPPORT] = 'addSchoolSupport';
  handlers[sdcActionsConstant.UPDATE_SCHOOL_SUPPORT] = 'updateSchoolSupport';
  handlers[sdcActionsConstant.SET_ALL_SCHOOL_SUPPORTS] = 'setAllSchoolSupports';
  // User Actions
  handlers[sdcActionsConstant.UPDATE_GAP_PLAN] = 'updateGapPlan';

  handlers[sdcActionsConstant.ADD_STUDENT_SUPPORTS] = 'addStudentSupports';
  handlers[sdcActionsConstant.UPDATE_STUDENT_SUPPORTS] = 'updateStudentSupports';
  handlers[sdcActionsConstant.SET_ALL_STUDENT_SUPPORTS] = 'setAllStudentSupports';
  handlers[sdcActionsConstant.SET_ALL_STUDENTS] = 'setAllStudents';

  let validate: string;
  let validationBehavior: 'notify';

  const defaultSdcViewState = {
    grid: {
      // Ag grid style filter - keys are columns, values depend on column type
      filter: null,
      sort: null,
      gridView: null,
      userAddedColumns: [],
    },
    profile: {
      filter: null,
      sort: null,
    },
    wizard: {
      filter: null,
      activeWizardStep: 1,
    },
  };

  let initialSdcState: ISdcStoreState = {
    ui: {
      isLoading: false,
      pendingSaves: {},
      currentActivity: {
        state: null,
      },
    },
    school: null,
    filter: {},
    sort: [],
    lockedFilters: [],
    selectedStudentIds: [],
    students: [],

    // "loadedProjection" is the projection used to fetch data for the students
    //  currently loaded in the store. This is the sum of projections needed
    //  to satisfy the defined 'columnKeys' and required 'projection' for an SDC activity
    loadedProjection: {},
    columnKeys: [],
    hiddenColumnKeys: [],
    projection: [],

    // Similar to 'loadedProjection' above
    loadedJoins: [],
    joins: [],
    recentEditedStudentIds: [],
    flattenedStudents: [],

    currentSdcState: {
      'grad-planning': defaultSdcViewState,
      'regents-prep': defaultSdcViewState,
      'regents-planning': defaultSdcViewState,
      'credit-gaps': defaultSdcViewState,
      attendance: defaultSdcViewState,
      'marking-period': defaultSdcViewState,
      'default-sdc': defaultSdcViewState,
    },

    // filtered, filteredFlattenedStudents, and filteredStudents are calculated by _refilterStudents()
    filtered: {
      flattenedStudents: [],
      students: [],
    },

    filteredFlattenedStudents: [],

    filteredStudents: [],

    users: {
      allSchoolUsers: [],
    },
    /**
     * Have discussed in engineering future need to nest states, first case being:
     *
     * school: {
     *  schoolSupports: any[];
     * }
     * etc..
     *
     * However changing existing `school` state into a a parent state as this
     * impacts other internal / external calls to the school state
     *
     * Should pursue that on a separate PR
     *
     * - JYR
     */
    schoolSupports: [],
    studentSupports: [],
  };

  // Only run the validator in DEV_MODE because it is slow
  if (PublicConfig.DEV_MODE) {
    validate = SDCStoreValidator.validate;
    validationBehavior = 'notify';
  }

  const SdcStore: ISdcStore = {
    initialize() {
      this.state = this.immutable(initialSdcState, {
        lazyMonkeys: true,
        validate,
        validationBehavior,
      });

      // This section of code sets up the pusher listener for the current school. When the school changes, the
      // previous pusher listener is destroyed and a new one is created for the new school.
      let schoolChannel;
      const schoolCursor = this.state.select('school');
      schoolCursor.on('update', () => {
        const schoolId = schoolCursor.get('_id');
        if (schoolId) {
          if (schoolChannel) schoolChannel.destroy();
          schoolChannel = new SDCStoreSchoolChannel(schoolId);
        }
      });
    },

    handlers,

    setFilterForColumn(payload) {
      this.addFilters({ newFilters: { [payload.columnName]: payload.newFilter } });
    },

    addFilters(payload) {
      const self = this;
      _.each(payload.newFilters, function(value, key) {
        self.state.set(['filter', key], value);
      });
      this.setRecentEditedStudentIds({ _ids: [] });
      this.state.set('selectedStudentIds', []);

      this._refilterStudents();
    },

    clearFilterForColumn(payload) {
      this.state.unset(['filter', payload.columnName]);
      const newLockedFilters = _.pull(UtilitiesService.copyPOJO(this.state.get('lockedFilters')), payload.columnName);
      if (!angular.equals(this.state.get('lockedFilters'), newLockedFilters)) {
        this.state.set('lockedFilters', newLockedFilters);
      }
      this.setRecentEditedStudentIds({ _ids: [] });
      this.state.set('selectedStudentIds', []);

      this._refilterStudents();
    },

    setEntireFilter({ newFilter, newLockedFilters }) {
      const existingFilter = this.state.get('filter');

      // If filters have changed, reset recently edited/selected students
      const filtersAreSame = angular.equals(existingFilter, newFilter);
      if (!filtersAreSame) {
        this.setRecentEditedStudentIds({ _ids: [] });
        this.state.set('selectedStudentIds', []);
      }

      // Set filter with new filter
      this.state.set('filter', newFilter);

      const newFilterKeys = _.keys(newFilter);

      if (newLockedFilters) {
        newLockedFilters = _.intersection<string>(newFilterKeys, newLockedFilters);
        if (newLockedFilters.length !== newLockedFilters.length) {
          const unfilteredCols = JSON.stringify(_.difference(newLockedFilters, newLockedFilters));
          console.warn("Cannot lock these columns, because they aren't filtered: " + unfilteredCols);
        }
      } else {
        // This ensures that if a column that was previously filtered and locked is no longer filtered we remove it
        // from the lockedFilters array
        newLockedFilters = _.intersection(this.state.get('lockedFilters'), newFilterKeys);
      }

      // Check to see if we are actually changing the lockedFilters array. If we aren't don't call `state.set()` at
      // all (this avoids thrashing).
      const existingLockedFilters = this.state.get('lockedFilters');
      const lockedFiltersAreSame = angular.equals(existingLockedFilters, newLockedFilters);
      if (!lockedFiltersAreSame) {
        this.state.set('lockedFilters', newLockedFilters);
      }

      this._refilterStudents();
    },

    setSort(payload: { sort: IColumnSort }) {
      if (!angular.equals(this.state.get('sort'), payload.sort)) {
        this.state.set('sort', payload.sort);
      }
    },

    clearAllUnlockedFilters() {
      if (this.state.get('lockedFilters').length) {
        const lockedFilters = this.state.get('lockedFilters');
        const diff = _.difference(
          _.keys(this.state.get('filter')),
          _.keys(
            _.pickBy(
              this.state.get('filter'),
              _.each(lockedFilters, function(v) {
                return v;
              }),
            ),
          ),
        );
        let filtersToApply = _.omit(this.state.get('filter'), diff);
        this.state.set('filter', filtersToApply);
      } else {
        this.state.set('filter', {});
      }
      this.setRecentEditedStudentIds({ _ids: [] });
      this.state.set('selectedStudentIds', []);
    },

    addLockedFilters(payload) {
      let existingLockedFilters = this.state.get('lockedFilters');
      let locksToAdd = _.union(existingLockedFilters, payload.newLocks);
      this.state.set('lockedFilters', locksToAdd);
    },

    setLockedFilters(payload) {
      const existingFilters = _.keys(this.state.get('filter'));
      const filtersToLock = _.intersection(existingFilters, payload.lockedFilters);
      if (filtersToLock.length !== existingFilters.length) {
        const unfilteredCols = JSON.stringify(_.difference(payload.lockedFilters, filtersToLock));
        console.warn("Cannot lock these columns, because they aren't filtered: " + unfilteredCols);
      }
      this.state.set('lockedFilters', filtersToLock);
    },

    unlockAllFilters() {
      this.state.set('lockedFilters', []);
    },

    setSelectedStudentIds(payload) {
      const ids = UtilitiesService.copyPOJO(payload.ids);
      this.state.set('selectedStudentIds', ids);
    },

    selectAllStudents() {
      const ids = _.map(this.state.get('students'), '_id').sort();
      this.state.set('selectedStudentIds', ids);
    },

    selectFilteredStudents() {
      const ids = _.map(this.state.get('filteredStudents'), '_id').sort();
      this.state.set('selectedStudentIds', ids);
    },

    unselectAllStudents() {
      this.state.set('selectedStudentIds', []);
    },

    setStudentsColumnKeysSchoolSortAndFilter({
      school,
      students,
      studentsProjection,
      studentsJoins,
      filter,
      columnKeys,
      hiddenColumnKeys,
      joins,
      sort,
    }) {
      // Also convert school to a POJO
      school = school && school.toPOJO ? school.toPOJO() : school;

      if (school) {
        this.state.set('school', school);
      } else {
        if (!this.state.get('school') && students.length) {
          throw new Error('SDCStore is invalid because students are defined with out a school');
        }
      }

      if (students.length > 0) {
        // this._setStudents(students); // TODO: should this be based on specific join?
        students = this._mergeStudents(students, studentsProjection, studentsJoins);
      } else {
        students = [];
        const flattenedStudents = [];
        this._setStudents(students);
        this.state.set('flattenedStudents', flattenedStudents);
        this.state.set('loadedProjection', studentsProjection);
        this.state.set('loadedJoins', studentsJoins);
      }

      const originalColumnKeys: TValidSdcColumns[] = this.state.get('columnKeys');
      const newColumnKeys: TValidSdcColumns[] = _.difference(columnKeys, originalColumnKeys);

      this.state.set('columnKeys', columnKeys);
      this.state.set('hiddenColumnKeys', hiddenColumnKeys || []);
      this.state.set('joins', joins);
      this.state.set('selectedStudentIds', []);
      this.setRecentEditedStudentIds({ _ids: [] });

      if (filter) {
        this._setFilterPickedToKeys(filter, columnKeys);
      }
      if (sort) {
        this.state.set('sort', sort);
      }

      if (newColumnKeys.length > 0) {
        // Flatten Students with new columns only
        const newFlattenedStudents = StudentSet.flattenStudents(students, school, newColumnKeys);
        // Merge new flattend fields onto existing flattened students
        this._mergeFlattenedStudents(newFlattenedStudents);
      }

      this._refilterStudents();
    },

    appendColumnData(payload) {
      // TODO: Bug. Sometimes payload.students is undefined?
      const students = this._mergeStudents(payload.students, payload.projection, payload.joins);
      let columnKeys = this.state.get('columnKeys');
      columnKeys = _.union(columnKeys, payload.columnKeys);
      this.state.set('columnKeys', columnKeys);
      const hiddenColumnKeys = _.intersection(columnKeys, this.state.get('hiddenColumnKeys'));
      this.state.set('hiddenColumnKeys', hiddenColumnKeys);

      const school = this.state.get('school');
      const flattenedStudents = StudentSet.flattenStudents(students, school, columnKeys);
      this.state.set('flattenedStudents', flattenedStudents);
      this._refilterStudents();
    },

    updateStudentFieldByPath(payload) {
      const { path, _ids } = payload;
      if (!path || path.length === 0) throw new Error('a path is required');

      // Send to ngrx store
      const patch = UtilitiesService.setFieldByPath({}, payload.path, payload.newValue);
      const updateMyStudentsAction = new BulkUpdateMyStudentsSuccess({ patches: [{ _ids, patch }] });
      const updateAttendanceStudentsAction = new BulkUpdateAttendanceStudentsSuccess({ patches: [{ _ids, patch }] });
      EventBus.emitAction(updateMyStudentsAction);
      EventBus.emitAction(updateAttendanceStudentsAction);

      let students = this.state.get('students');
      let recentEditedStudentIds;

      if (payload.bulk) {
        students = UtilitiesService.copyPOJO(students);

        _.each(payload._ids, function(_id) {
          let student = _.find(students, { _id });
          UtilitiesService.setFieldByPath(student, path, payload.newValue);
        });

        this._setStudents(students);
        recentEditedStudentIds = _.union(this.state.get('recentEditedStudentIds'), payload._ids);
      } else {
        const editedIds = [];
        _.each(payload._ids, _id => {
          const indx = _.findIndex(students, { _id });
          if (indx > -1) {
            const student = UtilitiesService.copyPOJO(students[indx]);
            UtilitiesService.setFieldByPath(student, path, payload.newValue);
            this.state.select('students').set(indx, student);
            editedIds.push(_id);
          }
        });

        recentEditedStudentIds = _.union(this.state.get('recentEditedStudentIds'), editedIds);
      }

      this.setRecentEditedStudentIds({ _ids: recentEditedStudentIds });
      this._updateFlattenedStudentsById(payload);
      this._refilterStudents();
    },

    /**
     * Apply multiple path/newValue combinations to multiple students
     * @param payload {Object} payload has a `patches` key where the value is an array of object like this:
     *   { _ids: ['id1', 'id2'], path: 'some.path', newValue: 'newValue' }
     */
    multiStudentUpdate(payload) {
      // Just convert the payload.patches to patches formatted like the format `multiStudentPatch` needs, and then
      // call that.
      const patches = _.map(payload.patches, (patch: any) => {
        return {
          studentIds: patch._ids,
          _ids: patch._ids, // This field is redundant, but needed for sending data to ngrx store(JJ)
          patch: UtilitiesService.setFieldByPath({}, patch.path, patch.newValue),
        };
      });

      // if request arrives from v3, bypass these actions
      if (payload.origin !== 'ngrx') {
        const updateMyStudentsAction = new BulkUpdateMyStudentsSuccess({ patches });
        const updateAttendanceStudentsAction = new BulkUpdateAttendanceStudentsSuccess({ patches });
        const updateStudentsAction = new BulkUpdateStudentsSuccess({ patches });

        EventBus.emitAction(updateStudentsAction);
        EventBus.emitAction(updateMyStudentsAction);
        EventBus.emitAction(updateAttendanceStudentsAction);
      }

      this.multiStudentPatch({ patches });
    },

    /**
     * Apply multiple patches to multiple students
     * @param payload {Object} an object with a `patches` key that is an array of objects like this:
     *    { studentIds: ['id1', 'id2'], patch: { .. the patch ..  } }
     */
    multiStudentPatch(payload) {
      const school: ISchool = this.state.get('school');
      const columnKeys = this.state.get('columnKeys');
      const studentsCursor = this.state.select('students');
      const flattenedStudentsCursor = this.state.select('flattenedStudents');
      const studentUpdates = _.reduce(
        payload.patches,
        (updates, patch: any) => {
          _.each(patch.studentIds, id => {
            const combinedPatch = updates[id] ? UtilitiesService.copyPOJO(updates[id]) : {};
            let currentPatch = _.cloneDeep(patch.patch);

            Object.keys(currentPatch).forEach(path => {
              if (path === 'pointPeople') {
                const patch = currentPatch[path];
                const isPointPeoplePatch =
                  patch.length && patch[0].hasOwnProperty('_id') && patch[0].hasOwnProperty('value');
                if (isPointPeoplePatch) {
                  const currentStudentPatch: any = _.find(currentPatch[path], { _id: id });
                  currentPatch = { [path]: currentStudentPatch.value };
                }
              }
            });
            UtilitiesService.patchObject(combinedPatch, currentPatch);
            updates[id] = combinedPatch;
          });
          return updates;
        },
        {},
      );

      const students = studentsCursor.get();
      _.each(studentUpdates, (combinedPatch, id) => {
        const idx = _.findIndex(students, { _id: id });
        if (idx > -1) {
          const student = UtilitiesService.copyPOJO(students[idx]);
          UtilitiesService.patchObject(student, combinedPatch);
          const flattenedStudent = StudentSet.flattenStudents([student], school, columnKeys)[0];
          studentsCursor.set(idx, student);
          flattenedStudentsCursor.set(idx, flattenedStudent);
        }
      });

      this._refilterStudents();

      const recentEditedStudentIds = _.union(this.state.get('recentEditedStudentIds'), _.keys(studentUpdates));

      if (!payload.ignoreRecentlyUpdatedStudents) {
        this.setRecentEditedStudentIds({ _ids: recentEditedStudentIds, ignoreRecentlyEditedStudents: true });
      }
    },

    updateUiState(payload) {
      if (!_.isUndefined(payload.isLoading)) {
        this.state.select('ui').set('isLoading', payload.isLoading);
      }
      if (!_.isUndefined(payload.currentActivity)) {
        this.state.select('ui').merge('currentActivity', payload.currentActivity);
      }
    },

    setRecentEditedStudentIds(payload) {
      this.state.set('recentEditedStudentIds', payload._ids);
    },

    addPendingSave(payload) {
      this.state.select(['ui', 'pendingSaves']).set(payload._id, payload.pendingSave);
    },

    removePendingSave(payload) {
      this.state.select(['ui', 'pendingSaves']).unset(payload._id);
    },

    // TODO - if too slow, can be made more efficient by directly updating
    // students in this method instead of calling `this.multiStudentUpdate`.
    addStudentSupports(payload) {
      const storedStudentSupports = this.state.get('studentSupports');
      const storedStudents = this.state.get('students');
      const { studentIdsToStudentSupportIds, studentSupport } = payload;
      // guard
      if (!storedStudentSupports || !studentIdsToStudentSupportIds) return;
      const path = 'join_studentSupports';
      const studentSupportsToAdd = [];

      // remove studentIds that already have a studentSupport for the supportId
      // reduce array of objects that look like { studentId: studentSupportId }
      const patches = _.reduce(
        studentIdsToStudentSupportIds,
        (patches, studentSupportId: string, studentId) => {
          const studentToUpdate = _.find(storedStudents, (storedStudent: any) => {
            return storedStudent._id === studentId;
          });

          const joinsToUpdate = _.get(studentToUpdate, path);

          if (studentToUpdate && joinsToUpdate) {
            /**
             * Checking studentJoins. If ss doesn't existing on join,
             * would it even exist in studentSupports? - JYR
             */
            const studentSupportExists = _.some(joinsToUpdate, join_studentSupport => {
              return join_studentSupport._id === studentSupportId;
            });

            if (!studentSupportExists) {
              const mutableStudentSupport = _.cloneDeep(studentSupport);
              const mutableStudentToUpdate = _.cloneDeep(studentToUpdate);
              const mutableJoins = angular.copy(joinsToUpdate);

              /**
               * newly assigned studentSupports don't initially have all the fields needed
               * by a view that is pulling its studentSupports data off the store. Adding them here
               */
              mutableStudentSupport._id = studentSupportId;
              mutableStudentSupport.student.studentId = studentId;
              const { last, first } = mutableStudentToUpdate.studentDetails.name;
              mutableStudentSupport.student.lastFirst = `${last}, ${first}`;

              const pojoStudentSupport = UtilitiesService.copyPOJO(mutableStudentSupport);
              const pojoStudentJoins = UtilitiesService.copyPOJO(mutableJoins);

              // update `students`
              pojoStudentJoins.push(pojoStudentSupport);
              const studentToUpdateIndex = _.findIndex(storedStudents, { _id: studentId });
              storedStudents[studentToUpdateIndex].join_studentSupports = pojoStudentJoins;

              // update `studentSupports`
              studentSupportsToAdd.push(pojoStudentSupport);

              // multiStudentUpdate
              const patch = { _ids: [studentId], path, newValue: pojoStudentJoins };
              patches.push(patch);
            }
          }
          return patches;
        },
        [],
      );

      const updatedStudentSupports = storedStudentSupports.concat(studentSupportsToAdd);
      this.state.set('studentSupports', updatedStudentSupports);
      this.state.set('students', storedStudents);
      return this.multiStudentUpdate({ patches });
    },

    // This fn() allows for patching multiple studentSupport records.
    updateStudentSupports(payload) {
      if (!payload.studentSupportIds) return;
      const { studentSupportIds, patch } = payload;
      const path = 'join_studentSupports';
      const storedStudentSupports = this.state.get('studentSupports');
      const storedStudents = this.state.get('students');

      // guard against attempts to check store when on SSV.
      if (!storedStudents || !storedStudentSupports) return;

      const studentSupportToUpdate = _.find(storedStudentSupports, (storedStudentSupport: any) => {
        return storedStudentSupport._id === studentSupportIds[0];
      });

      if (!studentSupportToUpdate) {
        const debugErr = `Support with _id ${studentSupportIds[0]} not found in store`;
        Rollbar.warning(debugErr);
        console.warn('Exiting early');
        return;
      }

      const { _id: studentSupportId, student } = studentSupportToUpdate;
      const mutableStudentSupport = angular.copy(studentSupportToUpdate);
      const studentSupportIndex = _.findIndex(storedStudentSupports, { _id: studentSupportId });
      _.each(patch, (value, field) => {
        mutableStudentSupport[field] = value;
      });
      const pojoStudentSupport = UtilitiesService.copyPOJO(mutableStudentSupport);
      /**
       * TODO: this is the first store update method in which we have to account for
       * keeping multiple states in sync, in this case `students` and `studentSupports`.
       * Feels brittle. How does a dev know what other state might potentially
       * need to be updated when data is shared between them?  - JYR
       */
      try {
        // swap out the studentSupport in the store
        storedStudentSupports[studentSupportIndex] = pojoStudentSupport;
        this.state.set('studentSupports', storedStudentSupports);
      } catch (error) {
        if (error.message.includes('Cannot assign to read only property')) {
          // when develop(host) locally, will have 'Cannot assign to read only property' issue
          // swap out the studentSupport in the store
          const updatedStudentSupports = angular.copy(storedStudentSupports);
          updatedStudentSupports[studentSupportIndex] = pojoStudentSupport;
          this.state.set('studentSupports', updatedStudentSupports);
        } else {
          // do nothing ...
        }
      }

      // Ignore loading storedStudent when store is empty.
      if (storedStudents.length) {
        const studentToUpdate = _.find(storedStudents, (storedStudent: any) => {
          return storedStudent._id === student.studentId;
        });
        const studentToUpdateIndex = _.findIndex(storedStudents, (storedStudent: any) => {
          return storedStudent._id === student.studentId;
        });
        // get joins & idx
        let joinsToUpdate: any[] = _.get(studentToUpdate, path);
        if (!joinsToUpdate) joinsToUpdate = [];
        let joinsIndex: number = _.findIndex(joinsToUpdate, { _id: studentSupportId });
        // if not previously joined
        if (joinsIndex === -1) joinsIndex = 0;
        const mutableStudentToUpdate = angular.copy(studentToUpdate);
        const mutableJoins = angular.copy(joinsToUpdate); // array of multiple
        _.each(patch, (value, field) => {
          if (!mutableJoins.length) {
            mutableJoins.push({ field: value });
          } else {
            mutableJoins[joinsIndex][field] = value;
          }
        });

        mutableStudentToUpdate.join_studentSupports = mutableJoins;
        const pojoStudentJoins = UtilitiesService.copyPOJO(mutableJoins);
        const pojoStudent = UtilitiesService.copyPOJO(mutableStudentToUpdate);
        storedStudents[studentToUpdateIndex] = pojoStudent;
        this.state.set('students', storedStudents);
        const patches = [{ _ids: [student.studentId], path, newValue: pojoStudentJoins }];
        return this.multiStudentUpdate({ patches });
      }
    },

    addCourseDiffs(payload) {
      const { studentIdsToCourseDiffIds, courseDiff } = payload;
      const students = this.state.get('students');
      const path = 'join_pendingCourseDiffs';
      const patches = _.reduce(
        studentIdsToCourseDiffIds,
        (patches, courseDiffId, student_id) => {
          const student = _.find(students, { _id: student_id });
          const join_pendingCourseDiffs = _.get(student, path);

          if (student && join_pendingCourseDiffs) {
            const courseDiffExists = _.some(join_pendingCourseDiffs, join_pendingCourseDiff => {
              return join_pendingCourseDiff._id === courseDiffId;
            });

            if (!courseDiffExists) {
              const join_courseDiffToBeAdded = UtilitiesService.copyPOJO(courseDiff);
              const newJoin_courseDiffs = UtilitiesService.copyPOJO(join_pendingCourseDiffs);
              const patch = { _ids: [student_id], path, newValue: newJoin_courseDiffs };
              join_courseDiffToBeAdded._id = courseDiffId;
              newJoin_courseDiffs.push(join_courseDiffToBeAdded);
              patches.push(patch);
            }
          }
          return patches;
        },
        [],
      );

      return this.multiStudentUpdate({ patches });
    },

    addGapPlans(payload) {
      const { studentIdsToGapPlanIds, gapPlan } = payload;
      const students = this.state.get('students');
      const path = 'join_activeGapPlans';
      const patches = _.reduce(
        studentIdsToGapPlanIds,
        (patches, gapPlanId, student_id) => {
          const student = _.find(students, { _id: student_id });
          const join_activeGapPlans = _.get(student, path);

          if (student && join_activeGapPlans) {
            const gapPlanExists = _.some(join_activeGapPlans, join_activeGapPlans => {
              return join_activeGapPlans._id === gapPlanId;
            });
            if (!gapPlanExists) {
              const join_gapPlanToBeAdded = UtilitiesService.copyPOJO(gapPlan);
              const newJoin_gapPlans = UtilitiesService.copyPOJO(join_activeGapPlans);
              const patch = { _ids: [student_id], path, newValue: newJoin_gapPlans };

              join_gapPlanToBeAdded._id = gapPlanId;
              newJoin_gapPlans.push(join_gapPlanToBeAdded);
              patches.push(patch);
            }
          }
          return patches;
        },
        [],
      );

      return this.multiStudentUpdate({ patches });
    },

    addSchoolSupport(data) {
      /**
       * NOTES:
       * Object keys must match object keys defined in ISdcStore interface
       * Object contains `schoolIdToSchoolSupportIds` - may decide to remove.
       * - JYR
       */
      const { schoolSupport } = data;
      const existingSchoolSupports = this.state.get('schoolSupports');
      if (!existingSchoolSupports) return;
      const updatedSchoolSupports = _.cloneDeep(existingSchoolSupports.concat(schoolSupport));
      const schoolSupportExists = _.some(existingSchoolSupports, (existing: ISchoolSupport) => {
        if (existing._id && data.schoolSupport) return existing._id === data.schoolSupport._id;
      });
      if (!schoolSupportExists) this.state.set('schoolSupports', updatedSchoolSupports);
    },

    // This fn() assumes we are only patching one diff at time. This will not work on bulk updates!
    updateCourseDiff(courseDiff: ICourseDiff) {
      const {
        student: { studentId: student_id },
        _id: courseDiffId,
      } = courseDiff;
      const students = this.state.get('students');
      const student = _.find(students, { _id: student_id });
      const path = 'join_pendingCourseDiffs';
      const join_pendingCourseDiffs: any[] = _.get(student, path);

      if (!join_pendingCourseDiffs) return;

      const newJoin_courseDiffs = angular.copy(join_pendingCourseDiffs);
      const indexToSwap = _.findIndex(newJoin_courseDiffs, { _id: courseDiffId });

      const updatedCourseDiff = UtilitiesService.copyPOJO(courseDiff);
      // swap stale with updated
      newJoin_courseDiffs[indexToSwap] = updatedCourseDiff;
      const patches = [{ _ids: [student_id], path, newValue: newJoin_courseDiffs }];

      return this.multiStudentUpdate({ patches });
    },

    // This fn() assumes we are only patching one gapPlan at time. This will not work on bulk updates!
    updateGapPlan(gapPlan: IGapPlan) {
      const {
        student: { studentId: student_id },
        _id: gapPlanId,
      } = gapPlan;
      const students = this.state.get('students');
      const student = _.find(students, { _id: student_id });
      const path = 'join_activeGapPlans';
      const join_activeGapPlans: any[] = _.get(student, path);

      // Gap Plans have not been joined for students in this User's store,
      // Do not attempt to update.
      if (!join_activeGapPlans) {
        return;
      }

      const newJoin_gapPlans = angular.copy(join_activeGapPlans);
      const indexToSwap = _.findIndex(newJoin_gapPlans, { _id: gapPlanId });

      // TODO: to help debug rollbar #4220 (JC)
      if (indexToSwap === -1) {
        Rollbar.error(`gapPlan with _id ${gapPlanId} cannot be found`, gapPlan);
        return;
      }

      const updatedGapPlan = UtilitiesService.copyPOJO(gapPlan);
      // swap stale with updated
      newJoin_gapPlans[indexToSwap] = updatedGapPlan;
      const patches = [{ _ids: [student_id], path, newValue: newJoin_gapPlans }];

      this.multiStudentUpdate({ patches });
      return;
    },

    updateSchoolSupport(data) {
      const copied = angular.copy(this.state.get('schoolSupports'));
      if (!copied) return;
      const { schoolSupportId, patch } = data;
      const existingSchoolSupportToUpdate = _.find(copied, { _id: schoolSupportId });
      const existingSchoolSupportIndexToSwap = _.findIndex(copied, { _id: schoolSupportId });
      _.each(patch, (value, field) => {
        existingSchoolSupportToUpdate[field] = value;
      });
      const updatedSchoolSupport = Object.assign({}, existingSchoolSupportToUpdate);
      copied[existingSchoolSupportIndexToSwap] = updatedSchoolSupport;
      this.state.set('schoolSupports', copied);
    },

    updateSchoolByPatch(payload) {
      const patch = payload.patch;
      const school: ISchool = UtilitiesService.copyPOJO(this.state.get('school'));
      UtilitiesService.patchObject(school, patch);
      this.state.set('school', school);
    },

    resetFlattenedStudents() {
      const students = this.state.get('students');
      const school: ISchool = this.state.get('school');
      const columnKeys = this.state.get('columnKeys');
      const flattenedStudents = StudentSet.flattenStudents(students, school, columnKeys);
      this.state.set('flattenedStudents', flattenedStudents);
    },

    _updateFlattenedStudentsById(payload) {
      const _ids = payload._ids;
      const bulk = payload.bulk;
      const flattenedStudents = this.state.get('flattenedStudents');
      const students: IStudent[] = this.state.get('students');
      const school: ISchool = this.state.get('school');
      const columnKeys = this.state.get('columnKeys');

      if (bulk) {
        const flattenedStudents = UtilitiesService.copyPOJO(this.state.get('flattenedStudents'));

        _.each(_ids, _id => {
          const flattenedStudentsIndx = _.findIndex(flattenedStudents, { _id });
          if (flattenedStudentsIndx < 0) return;
          const student = _.find(students, { _id });

          flattenedStudents[flattenedStudentsIndx] = StudentSet.flattenStudents([student], school, columnKeys)[0];
        });

        this.state.set('flattenedStudents', flattenedStudents);
      } else {
        _.each(_ids, _id => {
          const flattenedStudentsIndx = _.findIndex(flattenedStudents, { _id });
          if (flattenedStudentsIndx < 0) return;
          const student = _.find(students, { _id });
          const flattenedStudent = StudentSet.flattenStudents([student], school, columnKeys)[0];

          this.state.select('flattenedStudents').set(flattenedStudentsIndx, flattenedStudent);
        });
      }
    },

    /**
     * Refilters all students. The `students`, `flattenedStudents` and `filter` parts of the store should be set
     * before calling this method.
     * @private
     */
    _refilterStudents() {
      const students = this.state.get('students');
      const flattenedStudents = this.state.get('flattenedStudents');
      const filter = this.state.get('filter');
      const filtered = StudentSet.filterStudents(students, flattenedStudents, filter);
      this.state.set('filtered', filtered);
      this.state.set('filteredStudents', filtered.students);
      this.state.set('filteredFlattenedStudents', filtered.flattenedStudents);
    },

    /**
     * Sets the filter, but only for columns present in columnKeys
     *
     * It is important to never have a filter set for a column that isn't present, otherwise no students will pass
     * the filter and `filteredStudents` will be `[]`.
     *
     * @param filter
     * @param columnKeys
     * @private
     */
    _setFilterPickedToKeys(filter, columnKeys) {
      this.state.set('filter', _.pickBy(filter, (value, key) => _.includes(columnKeys, key)));
    },

    /**
     * Merges students onto the store
     *
     * If the students property is empty, then just set it to the `students` arg. Otherwise, merge the `students`
     * arg onto the store.
     *
     * @param students {Array|Student} the new partial students to merge onto the existing students
     * @param studentsProjection {Object} the projection of the new students
     * @private
     */
    _mergeStudents(newStudents, studentsProjection, studentsJoins) {
      const studentsCursor = this.state.select('students');
      const storeStudents = studentsCursor.get();

      // Shortcut for a common case -- when the store is empty, we don't try to merge anything. Just set the
      // `students` array
      if (storeStudents.length === 0) {
        this._setStudents(newStudents);
        this._mergeLoadedProjection(studentsProjection);
        this._mergeLoadedJoins(studentsJoins);
        return newStudents;
      }

      // Make an intermediate map so that we can avoid continually looking up students with _.find
      const storeStudentMap = {};
      _.each(
        storeStudents,
        (storeStudent: IStudent, idx) => (storeStudentMap[storeStudent._id] = { idx, storeStudent }),
      );

      // Add studentsProjection to the existing projection
      this._mergeLoadedProjection(studentsProjection);

      // Add studentsJoins to the existing joins
      this._mergeLoadedJoins(studentsJoins);

      // If we're updating more than 30% of students, copy entire array to mutable objects
      // Copy one by one and reset entire array
      if (newStudents.length / storeStudents.length > 0.3) {
        const storeStudentsCopy = UtilitiesService.copyPOJO(storeStudents);
        _.each(newStudents, newStudent => {
          if (!storeStudentMap[newStudent._id]) {
            console.warn(`${newStudent._id} is not currently found in the stored students. Omitting student.`);
            Rollbar.warning(`${newStudent._id} is not currently found in the stored students. Omitting student.`);
          } else {
            const { idx } = storeStudentMap[newStudent._id];
            UtilitiesService.patchObject(storeStudentsCopy[idx], newStudent);
          }
        });
        this._setStudents(storeStudentsCopy);
        return storeStudentsCopy;
      } else {
        // Iterate over the students arg, merging students or adding them
        _.each(newStudents, newStudent => {
          const { idx, storeStudent } = storeStudentMap[newStudent._id];
          const storeStudentCopy = UtilitiesService.copyPOJO(storeStudent);
          UtilitiesService.patchObject(storeStudentCopy, newStudent);
          studentsCursor.set(idx, storeStudentCopy);
        });
        return studentsCursor.get();
      }
    },

    /**
     * Merges flattened students onto the store by _id or adding new students
     */
    _mergeFlattenedStudents(newFlattenedStudents) {
      const flattenedStudentsCursor = this.state.select('flattenedStudents');
      const storeFlattenedStudents: any[] = flattenedStudentsCursor.get();

      // Shortcut for a common case -- when the store is empty, we don't try to merge anything. Just set the
      // `students` array
      if (storeFlattenedStudents.length === 0) {
        this.state.set('flattenedStudents', newFlattenedStudents);
        return newFlattenedStudents;
      }

      // Make an intermediate map so that we can avoid continually looking up students with _.find
      const storeFlattenedStudentsMap = {};
      _.each(storeFlattenedStudents, function buildMap(storeFlattenedStudent, idx) {
        storeFlattenedStudentsMap[storeFlattenedStudent._id] = { idx, storeFlattenedStudent };
      });

      // If we're updating more than 30% of students, copy entire array to mutable objects
      // Copy one by one and reset entire array
      // TODO: May need to optimize the cutoff here. Especially when we start updating only a subset of students
      if (newFlattenedStudents.length / storeFlattenedStudents.length > 0.3) {
        const storeFlattenedStudentsCopy = UtilitiesService.copyPOJO(storeFlattenedStudents);
        _.each(newFlattenedStudents, function patchMutable(newFlattenedStudent) {
          const { idx } = storeFlattenedStudentsMap[newFlattenedStudent._id];
          UtilitiesService.patchObject(storeFlattenedStudentsCopy[idx], newFlattenedStudent);
        });
        this.state.set('flattenedStudents', storeFlattenedStudentsCopy);
        return storeFlattenedStudentsCopy;
      } else {
        // Iterate over the students arg, merging students or adding them
        _.each(newFlattenedStudents, function patchAndSet(newFlattenedStudent) {
          const { idx, storeFlattenedStudent } = storeFlattenedStudentsMap[newFlattenedStudent._id];
          const storeFlattenedStudentCopy = UtilitiesService.copyPOJO(storeFlattenedStudent);
          UtilitiesService.patchObject(storeFlattenedStudentCopy, newFlattenedStudent);
          flattenedStudentsCursor.set(idx, storeFlattenedStudentCopy);
        });
        // Add studentsProjection to the existing projection
        return flattenedStudentsCursor.get();
      }
    },

    _mergeLoadedProjection(newProjection) {
      const loadedProjection = this.state.get('loadedProjection');
      const mergedProjection = UtilitiesService.mergeProjection(loadedProjection, newProjection);
      this.state.set('loadedProjection', mergedProjection);
    },

    _mergeLoadedJoins(newJoins) {
      const loadedJoins = this.state.get('loadedJoins');
      const concatedJoins = loadedJoins.concat(newJoins);
      this.state.set('loadedJoins', concatedJoins);
    },

    _setStudents(students: any[]) {
      if (!Array.isArray(students)) throw new Error(`_setStudents expects an array`);
      this.state.set('students', students);
    },

    setSdcActivity(payload) {
      this.state.select(['ui', 'currentActivity']).set('state', payload.state);
    },

    setCurrentSdcState(payload) {
      const { sdcKey, sdcView, property, value } = payload;
      this.state.select(['currentSdcState', sdcKey, sdcView]).set(property, value);
    },

    resetCurrentSdcState() {
      const currentSdcState = this.state.get('currentSdcState');
      const resetSdcState = _.reduce(
        currentSdcState,
        (a, val, key) => {
          a[key] = _.cloneDeep(defaultSdcViewState);
          return a;
        },
        {},
      );
      this.state.set('currentSdcState', resetSdcState);
    },

    mergeUserAddedSdcColumns({ sdcKey, sdcView, columnsToAdd }) {
      const property = 'userAddedColumns';
      const currentUserAddedColumns = this.state.get(['currentSdcState', sdcKey, sdcView, property]);
      const value = currentUserAddedColumns.concat(columnsToAdd);
      this.setCurrentSdcState({
        sdcKey,
        sdcView,
        property,
        value,
      });
    },

    setAllSchoolUsers({ allSchoolUsers }) {
      this.state.set(['users', 'allSchoolUsers'], allSchoolUsers);
    },

    setAllSchoolSupports(allSchoolSupports) {
      this.state.set('schoolSupports', allSchoolSupports);
    },

    setAllStudentSupports(allStudentSupports) {
      this.state.set('studentSupports', allStudentSupports);
    },

    setAllStudents(allStudents) {
      this.state.set('students', allStudents);
    },

    exports: {
      getFilter() {
        return this.state.get('filter');
      },
      getSort() {
        return this.state.get('sort');
      },
      getLockedFilters() {
        return this.state.get('lockedFilters');
      },
      getSelectedStudentIds() {
        return this.state.get('selectedStudentIds');
      },
      getSelectedFlattenedStudents() {
        const ids = new Set(this.state.get('selectedStudentIds'));
        return _.filter(this.state.get('flattenedStudents'), (flattenedStudent: any) => {
          return ids.has(flattenedStudent._id);
        });
      },
      getAllStudents() {
        return this.state.get('students');
      },
      getAllFlattenedStudents() {
        return this.state.get('flattenedStudents');
      },
      // filter and includeRecentlyEdited are both optional, and default to the current filter and false,
      // respectively
      getFilteredStudents(filter, includeRecentlyEdited) {
        let filteredStudents;
        let students;

        if (!filter) {
          // if not asking for a custom filter, then used pre-calculated value
          filteredStudents = this.state.get('filteredStudents');
        } else {
          students = this.state.get('students');
          let flattenedStudents = this.state.get('flattenedStudents');
          let filtered = StudentSet.filterStudents(students, flattenedStudents, filter);
          filteredStudents = filtered.students;
        }

        if (!includeRecentlyEdited) {
          return filteredStudents;
        } else {
          const ret = [];
          const filteredIds = [];
          _.each(filteredStudents, (s: any) => {
            ret.push(s);
            filteredIds.push(s._id);
          });

          students = students || this.state.get('students');

          _.each(this.state.get('recentEditedStudentIds'), _id => {
            if (!_.includes(filteredIds, _id)) {
              ret.push(_.find(students, { _id }));
            }
          });

          return ret;
        }
      },
      // filter and includeRecentlyEdited are both optional, and default to the current filter and false,
      // respectively
      getFilteredFlattenedStudents(filter, includeRecentlyEdited) {
        let filteredFlattenedStudents;
        let flattenedStudents;

        if (!filter) {
          // if not asking for a custom filter, then used the pre-calculated value
          filteredFlattenedStudents = this.state.get('filteredFlattenedStudents');
        } else {
          flattenedStudents = this.state.get('flattenedStudents');
          let students = this.state.get('students');

          let filtered = StudentSet.filterStudents(students, flattenedStudents, filter);
          filteredFlattenedStudents = filtered.flattenedStudents;
        }

        if (!includeRecentlyEdited) {
          return filteredFlattenedStudents;
        } else {
          const ret = [];
          const filteredIds = [];
          _.each(filteredFlattenedStudents, (s: any) => {
            ret.push(s);
            filteredIds.push(s._id);
          });

          flattenedStudents = flattenedStudents || this.state.get('flattenedStudents');

          _.each(this.state.get('recentEditedStudentIds'), _id => {
            if (!_.includes(filteredIds, _id)) {
              ret.push(_.find(flattenedStudents, { _id }));
            }
          });

          return ret;
        }
      },
      getColumnKeys() {
        return this.state.get('columnKeys');
      },
      getHiddenColumnKeys() {
        return this.state.get('hiddenColumnKeys');
      },
      getRecentEditedStudentIds() {
        return this.state.get('recentEditedStudentIds');
      },
      getUiState() {
        return this.state.get('ui');
      },
      getSdcKeyFromUiState() {
        const uiState = this.exports.getUiState();
        const activity = uiState && uiState.currentActivity && uiState.currentActivity.state;
        const activeSdcStateArr = activity && activity.split('.');
        if (_.isArray(activeSdcStateArr)) {
          return activeSdcStateArr.pop();
        }
        // Activity is null or undefined
        return activity;
      },
      hasPendingSaves() {
        return _.keys(this.state.get(['ui', 'pendingSaves'])).length > 0;
      },
      getSchool() {
        return this.state.get('school');
      },
      getLoadedProjection() {
        return this.state.get('loadedProjection');
      },
      getLoadedJoins() {
        return this.state.get('loadedJoins');
      },
      getCurrentSdcState(sdcKey) {
        return this.state.get(['currentSdcState', sdcKey]);
      },
      getAllSchoolUsers() {
        return this.state.get(['users', 'allSchoolUsers']);
      },
      getAllSchoolSupports() {
        return this.state.get('schoolSupports');
      },
      getAllStudentSupports() {
        return this.state.get('studentSupports');
      },
    },
  };
  return SdcStore;
};
